import { motion, AnimatePresence } from 'framer-motion';
import { FiLinkedin, FiMail } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { FC, createElement, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useChatBot } from '../../context/ChatBotContext';

interface SocialLink {
  name: string;
  Icon: IconType;
  url: string;
  isInternal?: boolean;
}

const socialLinks: SocialLink[] = [
  {
    name: 'LinkedIn',
    Icon: FiLinkedin,
    url: 'https://linkedin.com/in/omar-belaid/',
    isInternal: false
  },
  {
    name: 'Email',
    Icon: FiMail,
    url: '/contact',
    isInternal: true
  },
];

const roles = [
  "Automatisations",
  "Solutions Web",
  "Intégration IA",
  "Chatbots IA",
  "Formations",
  "Automatisations",
  "Solutions Web",
  "Intégration IA",
  "Chatbots IA",
  "Formations",
  "Automatisations",
  "Solutions Web",
  "Intégration IA",
  "Chatbots IA",
  "Formations",
  "Automatisations",
  "Solutions Web",
  "Intégration IA",
  "Chatbots IA",
  "Formations"
];

export const Hero: FC = () => {
  const { openChat } = useChatBot();
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRoleIndex((prevIndex) => (prevIndex + 1) % roles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden pt-16">
      {/* Image de fond avec effet de flou */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/80 to-white/95 dark:via-gray-900/90 dark:to-gray-900/95 z-10" />
        <img
          src="/images/omar.jpg"
          alt="Portrait"
          className="w-full h-full object-cover opacity-15 dark:opacity-5 scale-110 blur-sm"
        />
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center relative z-20">
        {/* Photo de profil */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="w-48 h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 rounded-full overflow-hidden border-4 border-white/90 dark:border-gray-800/90 shadow-2xl mx-auto bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm">
            <img
              src="/images/omar.jpg"
              alt="Portrait"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            <span className="block mb-2">Omar Belaid</span>
            {/* <span className="block mb-2">Expertise IA</span> */}
            <AnimatePresence mode="wait">
              <motion.span
                key={currentRoleIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="text-indigo-600 dark:text-indigo-400 block"
              >
                {roles[currentRoleIndex]}
              </motion.span>
            </AnimatePresence>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl sm:text-2xl text-gray-600 dark:text-gray-300 mb-10 max-w-3xl mx-auto"
          >
            Je conçois des applications web rapides et fiables ; grâce à l’IA et aux automatisations, vos processus deviennent simples et efficaces.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex justify-center space-x-8 mb-12"
          >
            {socialLinks.map(({ name, Icon, url, isInternal }) => (
              isInternal ? (
                <Link
                  key={name}
                  to={url}
                  className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-200"
                  aria-label={name}
                >
                  {createElement(Icon as any, { size: 28 })}
                </Link>
              ) : (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-200"
                  aria-label={name}
                >
                  {createElement(Icon as any, { size: 28 })}
                </a>
              )
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="flex justify-center items-center space-x-4"
          >
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-3 border border-transparent text-lg font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Contactez moi !
            </Link>
            <Link
              to="/projects"
              className="inline-flex items-center px-8 py-3 border border-indigo-600 dark:border-indigo-400 text-lg font-medium rounded-md text-indigo-600 dark:text-indigo-400 bg-transparent hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Voir mes projets et collaborations
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}; 